import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import {
  Archive, BellRing, CalendarClock, FileText, Folder, History, Loader2, Pencil, RefreshCw, Sparkles, Target, X,
} from 'lucide-react';
import { apiService } from '../../services/api';

// Carnet de Muse sur le projet : les notes qu'elle tient à jour (chaque fait avec sa source, corrigeables par
// l'équipe, versionnées) et ses veilles (à une date, ou quand un objectif change d'état).

const SOURCE_RE = /\[((?:journal|meeting|tactical|point|objective|facture):\d+)\]/g;
const SOURCE_LABELS = { journal: 'journal', meeting: 'réunion', tactical: 'tactique', point: 'point', objective: 'objectif', facture: 'facture' };
const BASE_ORDER = ['synthese', 'decisions', 'questions-ouvertes', 'risques', 'chronologie'];
const STATUS_LABELS = { not_started: 'pas commencé', in_progress: 'en cours', blocked: 'bloqué', done: 'terminé' };

const formatDate = (value) => (value ? new Date(`${value.slice(0, 19)}Z`).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' }) : '');
const sourceLabel = (ref) => {
  const [kind, id] = ref.split(':');
  return `${SOURCE_LABELS[kind] || kind} ${id}`;
};

// « [meeting:5] » devient un lien qui ouvre la source.
const withSourceLinks = (content) => content.replace(SOURCE_RE, (_, ref) => `[${sourceLabel(ref)}](#source=${ref})`);

const SourceDialog = ({ projectId, sourceRef, onClose }) => {
  const [source, setSource] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    apiService.get(`/carnet/sources/${projectId}/${encodeURIComponent(sourceRef)}`).then(setSource).catch((err) => setError(err.message));
  }, [projectId, sourceRef]);
  const kind = sourceRef.split(':')[0];
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-900/40 px-4 py-10" onMouseDown={onClose}>
      <div className="w-full max-w-2xl rounded-2xl bg-white shadow-xl" onMouseDown={(event) => event.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 border-b border-slate-100 px-5 py-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-indigo-600">Source · {sourceLabel(sourceRef)}</p>
            <h3 className="text-base font-semibold text-slate-900">{source?.title || '…'}</h3>
            {source?.date && <p className="text-xs text-slate-500">{source.date}</p>}
          </div>
          <button type="button" onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100"><X size={16} /></button>
        </div>
        <div className="max-h-[70vh] overflow-y-auto px-5 py-4 text-sm text-slate-700">
          {error ? <p className="text-rose-600">{error}</p> : !source ? <Loader2 size={16} className="animate-spin text-slate-400" /> : (
            <>
              {source.summary && <div className="prose prose-sm max-w-none"><ReactMarkdown>{source.summary}</ReactMarkdown></div>}
              {source.text && <p className="whitespace-pre-line">{source.text}</p>}
              {source.transcript && (
                <details className="mt-3">
                  <summary className="cursor-pointer text-xs font-semibold text-slate-500">Transcription</summary>
                  <p className="mt-2 whitespace-pre-line text-xs text-slate-600">{source.transcript}</p>
                </details>
              )}
              {kind === 'meeting' && (
                <a href={`/meetings/${sourceRef.split(':')[1]}`} className="mt-3 inline-block text-xs font-semibold text-indigo-600 hover:underline">
                  Ouvrir la réunion
                </a>
              )}
              {kind === 'facture' && (
                <a href={`/expenses?facture=${sourceRef.split(':')[1]}`} className="mt-3 inline-block text-xs font-semibold text-indigo-600 hover:underline">
                  Ouvrir la facture
                </a>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const NoteView = ({ note, projectId, onSaved, onArchived, onSource }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(note.content);
  const [versions, setVersions] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => { setDraft(note.content); setEditing(false); setVersions(null); }, [note.id, note.content]);

  const save = async () => {
    try {
      onSaved(await apiService.put(`/carnet/notes/${note.id}`, { content: draft }));
      setEditing(false);
      setError(null);
    } catch (err) {
      setError(err.message);
    }
  };
  const archive = async () => {
    if (!window.confirm(`Retirer la note « ${note.title} » du carnet ?`)) return;
    await apiService.delete(`/carnet/notes/${note.id}`);
    onArchived(note);
  };
  const showHistory = async () => setVersions(versions ? null : await apiService.get(`/carnet/notes/${note.id}/versions`));

  return (
    <article className="min-w-0">
      <div className="mb-2 flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className="text-base font-semibold text-slate-900">{note.title}</h3>
          <p className="text-[11px] text-slate-400">
            {note.path} · v{note.version} · {note.by_human ? "corrigée par l'équipe" : 'par Nexus'} le {formatDate(note.updated_at)}
          </p>
        </div>
        <div className="flex items-center gap-1">
          <button type="button" onClick={() => setEditing((v) => !v)} title="Corriger"
            className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"><Pencil size={14} /></button>
          <button type="button" onClick={showHistory} title="Historique"
            className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"><History size={14} /></button>
          <button type="button" onClick={archive} title="Retirer"
            className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-rose-600"><Archive size={14} /></button>
        </div>
      </div>
      {editing ? (
        <div className="space-y-2">
          <textarea value={draft} onChange={(event) => setDraft(event.target.value)} rows={14}
            className="w-full rounded-xl border border-slate-200 p-3 font-mono text-xs outline-none focus:ring-2 focus:ring-indigo-100" />
          <p className="text-[11px] text-slate-400">Nexus respectera ta correction aux prochains passages.</p>
          <div className="flex gap-2">
            <button type="button" onClick={save} className="rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-700">Enregistrer</button>
            <button type="button" onClick={() => { setEditing(false); setDraft(note.content); }} className="rounded-lg px-3 py-1.5 text-xs text-slate-500 hover:bg-slate-100">Annuler</button>
          </div>
          {error && <p className="text-xs text-rose-600">{error}</p>}
        </div>
      ) : (
        <div className="prose prose-sm prose-slate max-w-none prose-headings:text-slate-800 prose-a:text-indigo-600 prose-a:no-underline">
          <ReactMarkdown components={{
            a: ({ href, children }) => (href?.startsWith('#source=') ? (
              <button type="button" onClick={() => onSource(href.slice(8))}
                className="mx-0.5 inline rounded bg-indigo-50 px-1 text-[10px] font-medium text-indigo-700 hover:bg-indigo-100">
                {children}
              </button>
            ) : <a href={href} target="_blank" rel="noreferrer">{children}</a>),
          }}>
            {withSourceLinks(note.content)}
          </ReactMarkdown>
        </div>
      )}
      {versions && (
        <div className="mt-3 rounded-xl bg-slate-50 p-3">
          <p className="mb-1 text-xs font-semibold text-slate-600">Versions précédentes</p>
          {versions.length === 0 ? <p className="text-xs text-slate-400">Aucune.</p> : versions.map((version) => (
            <details key={version.version} className="py-0.5 text-xs text-slate-600">
              <summary className="cursor-pointer">v{version.version} · {version.updated_by.startsWith('muse') ? 'Nexus' : 'équipe'} · {formatDate(version.created_at)}</summary>
              <pre className="mt-1 whitespace-pre-wrap font-sans text-[11px] text-slate-500">{version.content}</pre>
            </details>
          ))}
        </div>
      )}
    </article>
  );
};

const WatchList = ({ watches, onCancel }) => {
  if (!watches.length) return null;
  return (
    <div className="mt-4 border-t border-slate-100 pt-3">
      <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500"><BellRing size={13} /> Veilles de Nexus</p>
      <ul className="space-y-1.5">
        {watches.map((watch) => (
          <li key={watch.id} className={`flex items-start gap-2 text-sm ${watch.status === 'active' ? 'text-slate-700' : 'text-slate-400'}`}>
            {watch.kind === 'date' ? <CalendarClock size={14} className="mt-0.5 shrink-0" /> : <Target size={14} className="mt-0.5 shrink-0" />}
            <div className="min-w-0 flex-1">
              <p>{watch.instruction}</p>
              <p className="text-[11px] text-slate-400">
                {watch.kind === 'date' ? `le ${formatDate(watch.due_at)}`
                  : `quand « ${watch.objective_name || watch.objective_id} » change d'état${watch.statuses?.length ? ` (${watch.statuses.map((s) => STATUS_LABELS[s] || s).join(', ')})` : ''}`}
                {watch.notify_name ? ` · prévenir ${watch.notify_name}` : ''}
                {watch.status !== 'active' ? ` · ${{ fired: 'déclenchée', cancelled: 'annulée', expired: 'expirée' }[watch.status] || watch.status}` : ''}
                {watch.fired_detail ? ` — ${watch.fired_detail}` : ''}
              </p>
            </div>
            {watch.status === 'active' && (
              <button type="button" onClick={() => onCancel(watch)} title="Annuler la veille"
                className="rounded p-0.5 text-slate-300 hover:bg-slate-100 hover:text-slate-500"><X size={13} /></button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};

const MuseCarnet = ({ projectId, hideTitle = false }) => {
  const [data, setData] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [source, setSource] = useState(null);
  const [error, setError] = useState(null);
  const [requesting, setRequesting] = useState(false);

  const load = useCallback(async () => {
    try {
      setData(await apiService.get(`/carnet/projects/${projectId}`));
      setError(null);
    } catch (err) {
      setError(err.message);
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);
  const busy = data && ['queued', 'running'].includes(data.state.status);
  useEffect(() => {
    if (!busy) return undefined;
    const timer = setInterval(load, 15000);
    return () => clearInterval(timer);
  }, [busy, load]);

  const folders = useMemo(() => {
    const groups = {};
    (data?.notes || []).forEach((note) => {
      const parts = note.path.split('/');
      const folder = parts.length > 1 ? parts[0] : '';
      (groups[folder] = groups[folder] || []).push(note);
    });
    // Notes de base d'abord, dans l'ordre de lecture ; les autres par chemin.
    const rank = (note) => { const i = BASE_ORDER.indexOf(note.path); return i < 0 ? BASE_ORDER.length : i; };
    Object.values(groups).forEach((notes) => notes.sort((a, b) => rank(a) - rank(b) || a.path.localeCompare(b.path)));
    return Object.entries(groups).sort(([a], [b]) => (a === '' ? -1 : b === '' ? 1 : a.localeCompare(b)));
  }, [data]);

  const selected = data?.notes.find((n) => n.id === selectedId) || data?.notes.find((n) => n.path === 'synthese') || data?.notes[0];

  const refresh = async () => {
    setRequesting(true);
    try {
      await apiService.post(`/carnet/projects/${projectId}/refresh`, {});
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setRequesting(false);
    }
  };

  const replaceNote = (note) => setData((prev) => ({ ...prev, notes: prev.notes.map((n) => (n.id === note.id ? note : n)) }));
  const removeNote = (note) => setData((prev) => ({ ...prev, notes: prev.notes.filter((n) => n.id !== note.id) }));
  const cancelWatch = async (watch) => {
    await apiService.delete(`/carnet/watches/${watch.id}`);
    load();
  };

  const state = data?.state;
  return (
    <div>
      <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
        <div>
          {!hideTitle && <h2 className="flex items-center gap-2 text-base font-semibold text-slate-900"><Sparkles size={16} className="text-indigo-500" /> Carnet de Nexus</h2>}
          <p className="text-sm text-slate-500">
            {!data ? 'Chargement…'
              : busy ? 'Nexus met le carnet à jour…'
                : state.status === 'failed' ? `Dernier passage en échec : ${state.error || ''}`
                  : state.finished_at ? `Mis à jour le ${formatDate(state.finished_at)}${state.summary ? ` · ${state.summary}` : ''}`
                    : 'Nexus n\'a pas encore ouvert ce carnet.'}
            {data?.pending > 0 && !busy ? ` · ${data.pending} nouvelle${data.pending > 1 ? 's' : ''} entrée${data.pending > 1 ? 's' : ''} à intégrer` : ''}
          </p>
        </div>
        <button type="button" onClick={refresh} disabled={busy || requesting}
          className="flex items-center gap-1.5 rounded-lg border border-indigo-200 px-2.5 py-1.5 text-xs font-semibold text-indigo-700 hover:bg-indigo-50 disabled:opacity-50">
          {busy || requesting ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Mettre à jour
        </button>
      </div>
      {error && <p className="mb-2 text-sm text-rose-600">{error}</p>}

      {data && data.notes.length === 0 ? (
        <p className="rounded-xl border border-dashed border-slate-200 px-4 py-6 text-center text-sm text-slate-400">
          Pas encore de notes. Muse lit le journal, les réunions, les quoi de neuf et les points du projet, puis
          écrit sa synthèse, les décisions, les questions ouvertes…
        </p>
      ) : data && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-[220px_minmax(0,1fr)]">
          <nav className="space-y-2">
            {folders.map(([folder, notes]) => (
              <div key={folder || 'racine'}>
                {folder && <p className="flex items-center gap-1.5 px-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400"><Folder size={12} /> {folder}</p>}
                {notes.map((note) => (
                  <button key={note.id} type="button" onClick={() => setSelectedId(note.id)}
                    className={`flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm ${selected?.id === note.id ? 'bg-indigo-50 font-medium text-indigo-800' : 'text-slate-600 hover:bg-slate-50'}`}>
                    <FileText size={13} className="shrink-0 opacity-60" />
                    <span className="truncate">{note.title}</span>
                    {note.by_human && <Pencil size={10} className="ml-auto shrink-0 text-slate-400" />}
                  </button>
                ))}
              </div>
            ))}
          </nav>
          {selected && <NoteView note={selected} projectId={projectId} onSaved={replaceNote} onArchived={removeNote} onSource={setSource} />}
        </div>
      )}

      {data && <WatchList watches={data.watches} onCancel={cancelWatch} />}
      {source && <SourceDialog projectId={projectId} sourceRef={source} onClose={() => setSource(null)} />}
    </div>
  );
};

export default MuseCarnet;
